import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Video,
  Users,
  Calendar,
  Sparkles,
  ExternalLink,
  Play,
  MessageCircle,
  Search,
  Target,
  Wrench,
  ClipboardList,
  UserCheck,
  Ban,
  Info,
} from "lucide-react";
import { api, getUser, rupiah, youtubeId } from "../lib/api";
import { Header, Footer, Loading } from "../components/Shell";
import { useSeo, jsonLdService } from "../lib/seo";
import { useSiteContent, rich, splitList } from "../lib/siteContent";

const SOLUTION_ICONS = [<Search size={22} />, <ClipboardList size={22} />, <Target size={22} />, <Wrench size={22} />, <ArrowRight size={22} />];

function waLink(number, message) {
  if (!number) return "";
  const clean = String(number).replace(/^\+|\D/g, "");
  return `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
}

/* -------------------------------------------------------------------------- */
/*  Landing Page — Privat 1-on-1                                              */
/* -------------------------------------------------------------------------- */
export function PrivatLanding() {
  const [pkgs, setPkgs] = useState([]);
  const [settings, setSettings] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const [myMentoring, setMyMentoring] = useState([]);
  const [vipStatus, setVipStatus] = useState(null);
  const [mode, setMode] = useState("INDIVIDU"); // INDIVIDU | PERUSAHAAN
  const user = getUser();
  const { t: c, list } = useSiteContent("privat");
  const PROBLEMS = list("problem.items");
  const SOLUTION_STEPS = list("solution.steps").map((s, i) => ({ ...s, icon: SOLUTION_ICONS[i % SOLUTION_ICONS.length] }));
  const HOW_IT_WORKS = list("how.steps");
  const TOPICS = list("topics.groups").map((g) => ({ title: g.title, items: splitList(g.items) }));
  const WHAT_YOU_GET = list("benefits.items").map((x) => x.text);
  const USPS = list("usp.items");
  const WHO_JOIN = list("fit.yes_items").map((x) => x.text);
  const WHO_NOT_JOIN = list("fit.no_items").map((x) => x.text);
  const PRIVAT_FAQ = list("faq.items");

  useEffect(() => {
    api(`/mentoring/packages?mode=${mode}`).then(setPkgs).catch(() => {});
    api("/mentoring/settings").then(setSettings).catch(() => {});
    if (user?.id) {
      api("/me/mentoring").then(setMyMentoring).catch(() => {});
      api("/me/vip").then(setVipStatus).catch(() => {});
    }
    // Track view
    if (typeof window !== "undefined") {
      window.mastofaTrack?.("ViewContent", { content_name: "Privat 1-on-1", content_type: "product" });
    }
  }, [user?.id, mode]);

  const hasActiveMentoring = myMentoring.some((e) => e.status === "ACTIVE");
  const isVip = vipStatus?.active;
  const adminWa = settings.admin_whatsapp || "6285157009099";
  const bookingWaLink = waLink(
    adminWa,
    `Halo Admin Mas Tofa, saya ${user?.name || "[nama]"}. Saya sudah membeli Privat 1-on-1 dan ingin mengatur jadwal sesi.`,
  );
  const corpWaLink = waLink(
    settings.corporate_whatsapp || adminWa,
    "Halo Admin Mas Tofa, saya tertarik dengan paket Corporate untuk training tim.",
  );

  const heroCtaLabel = hasActiveMentoring
    ? "Lihat Privat Saya"
    : user
    ? "Pilih Paket"
    : "Ambil Sesi Privat";
  const heroCtaHref = hasActiveMentoring ? "/mentoring" : "#pricing";
  const pickTestId = hasActiveMentoring ? "view-mentoring-cta" : "privat-hero-cta";

  return (
    <div className="privat">
      <Header />
      <main className="privat-hero">
        <div className="privat-hero-inner">
          <span className="privat-eyebrow">{c("hero.eyebrow")}</span>
          <h1>{rich(c("hero.title"), "span", { className: "accent" })}</h1>
          <p>{c("hero.body")}</p>
          <div className="privat-trust-line">{c("hero.trust")}</div>
          <div className="privat-hero-cta">
            <a href={heroCtaHref} className="button" data-testid={pickTestId}>
              {heroCtaLabel} <ArrowRight size={18} />
            </a>
            <a href="#how" className="button button-ghost" data-testid="how-cta">
              {c("hero.cta2")}
            </a>
          </div>
          <div className="privat-hero-price">{rich(c("hero.price"), "b")}</div>
          {isVip && (
            <div className="privat-vip-notice" data-testid="vip-upsell-notice">
              <Info size={16} /> Anda sudah menjadi VIP Member. Untuk pembahasan yang lebih
              mendalam, upgrade ke sesi Privat 1-on-1.
            </div>
          )}
        </div>
      </main>

      <section className="privat-problem">
        <div className="section-head">
          <span className="section-kicker">{c("problem.kicker")}</span>
          <h2>{c("problem.title")}</h2>
        </div>
        <div className="privat-problem-grid">
          {PROBLEMS.map((p) => (
            <div
              key={p.title}
              className="privat-problem-card"
              data-testid={`privat-problem-${p.title.replace(/\s/g, "-")}`}
            >
              <h4>{p.title}</h4>
              <p>{p.body}</p>
            </div>
          ))}
        </div>
        <p className="privat-note">{c("problem.note")}</p>
      </section>

      <section className="privat-solution">
        <div className="section-head">
          <span className="section-kicker">{c("solution.kicker")}</span>
          <h2>{c("solution.title")}</h2>
          <p>{c("solution.body")}</p>
        </div>
        <div className="privat-method-grid privat-solution-grid">
          {SOLUTION_STEPS.map((s) => (
            <div key={s.title} className="privat-method-card" data-testid={`solution-${s.title}`}>
              <div className="privat-icon">{s.icon}</div>
              <small className="privat-step-num">{s.num}</small>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="how" className="privat-how">
        <div className="section-head">
          <span className="section-kicker">{c("how.kicker")}</span>
          <h2>{c("how.title")}</h2>
        </div>
        <ol className="privat-timeline">
          {HOW_IT_WORKS.map((s) => (
            <li key={s.step} data-testid={`how-${String(s.step).replace(" ", "-")}`}>
              <small>{s.step}</small>
              <b>{s.title}</b>
              <span>{s.body}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="privat-topics">
        <div className="section-head">
          <span className="section-kicker">{c("topics.kicker")}</span>
          <h2>{c("topics.title")}</h2>
          <p>{c("topics.body")}</p>
        </div>
        <div className="privat-topic-grid">
          {TOPICS.map((t) => (
            <div key={t.title} className="privat-topic-card" data-testid={`topic-${String(t.title).replace(/\s/g, "-")}`}>
              <h4>{t.title}</h4>
              <ul>{t.items.map((i) => (<li key={i}><CheckCircle2 size={13} /> {i}</li>))}</ul>
            </div>
          ))}
        </div>
        <p className="privat-note">{c("topics.note")}</p>
      </section>

      <section className="privat-benefits">
        <div className="section-head">
          <span className="section-kicker">{c("benefits.kicker")}</span>
          <h2>{c("benefits.title")}</h2>
        </div>
        <div className="privat-benefit-list" data-testid="benefit-list">
          {WHAT_YOU_GET.map((b) => (
            <div key={b}><CheckCircle2 size={16} /> {b}</div>
          ))}
        </div>
      </section>

      <section className="privat-usp">
        <div className="section-head">
          <span className="section-kicker">{c("usp.kicker")}</span>
          <h2>{c("usp.title")}</h2>
        </div>
        <div className="privat-usp-grid">
          {USPS.map((u) => (
            <div key={u.title} className="privat-usp-card" data-testid={`usp-${String(u.title).replace(/\s/g, "-")}`}>
              <h4>{u.title}</h4>
              <p>{u.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="privat-profile" data-testid="privat-profile">
        <div className="privat-profile-inner">
          <div className="privat-profile-photo">
            {c("profile.photo") ? <img src={c("profile.photo")} alt={c("profile.name")} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} /> : <UserCheck size={48} />}
          </div>
          <div>
            <span className="privat-eyebrow">{c("profile.eyebrow")}</span>
            <h2>{c("profile.name")}</h2>
            <p>{c("profile.body")}</p>
            <div className="privat-profile-tags">
              {splitList(c("profile.tags")).map((t) => (
                <span key={t} className="tag-chip">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="privat-fit">
        <div className="privat-fit-grid">
          <div className="privat-fit-card" data-testid="fit-yes">
            <div className="privat-fit-head yes"><CheckCircle2 size={20} /> {c("fit.yes_title")}</div>
            <ul>{WHO_JOIN.map((w) => <li key={w}>{w}</li>)}</ul>
          </div>
          <div className="privat-fit-card" data-testid="fit-no">
            <div className="privat-fit-head no"><Ban size={20} /> {c("fit.no_title")}</div>
            <ul>{WHO_NOT_JOIN.map((w) => <li key={w}>{w}</li>)}</ul>
          </div>
        </div>
        <p className="privat-note">{c("fit.note")}</p>
      </section>

      <section id="pricing" className="privat-pricing">
        <div className="section-head">
          <span className="section-kicker">{c("pricing.kicker")}</span>
          <h2>{c("pricing.title")}</h2>
        </div>
        <div className="privat-mode-toggle" data-testid="privat-mode-toggle" style={{
          display: "flex",
          justifyContent: "center",
          gap: 8,
          marginBottom: 32,
          background: "#f1f5f9",
          padding: 6,
          borderRadius: 10,
          maxWidth: 400,
          margin: "0 auto 32px",
        }}>
          <button
            onClick={() => setMode("INDIVIDU")}
            data-testid="mode-individu"
            style={{
              flex: 1,
              padding: "10px 20px",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              background: mode === "INDIVIDU" ? "#2563EB" : "transparent",
              color: mode === "INDIVIDU" ? "#fff" : "#475569",
              fontWeight: 600,
              transition: "all 0.15s",
            }}
          >
            👤 Individu
          </button>
          <button
            onClick={() => setMode("PERUSAHAAN")}
            data-testid="mode-perusahaan"
            style={{
              flex: 1,
              padding: "10px 20px",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              background: mode === "PERUSAHAAN" ? "#0f172a" : "transparent",
              color: mode === "PERUSAHAAN" ? "#fbbf24" : "#475569",
              fontWeight: 600,
              transition: "all 0.15s",
            }}
          >
            🏢 Perusahaan (2×)
          </button>
        </div>
        <div className="privat-pricing-grid">
          {pkgs.map((p) => {
            const showPrice = mode === "PERUSAHAAN" ? (p.corporate_price || p.price * 2) : p.price;
            const showRegular = mode === "PERUSAHAAN" ? (p.corporate_price_regular || p.price_regular * 2) : p.price_regular;
            return (
            <div
              key={p.slug}
              className={`privat-price-card ${p.badge === "Best Seller" ? "featured" : ""}`}
              data-testid={`privat-pkg-${p.slug}`}
            >
              {p.badge && <span className="privat-price-badge">{p.badge}</span>}
              {mode === "PERUSAHAAN" && (
                <span className="privat-price-badge" style={{ background: "#fbbf24", color: "#0f172a", marginLeft: 8 }}>
                  Perusahaan
                </span>
              )}
              <h3>{p.title}</h3>
              {showRegular > 0 && (
                <div className="privat-price-strike">{rupiah(showRegular)}</div>
              )}
              <div className="privat-price-main">{rupiah(showPrice)}</div>
              {p.per_session_label && mode === "INDIVIDU" && (
                <small className="privat-price-per">{p.per_session_label}</small>
              )}
              {mode === "PERUSAHAAN" && (
                <small className="privat-price-per">Training tim / korporat</small>
              )}
              <ul>
                <li>{p.tagline}</li>
                {(p.benefits || []).slice(0, 3).map((b) => (
                  <li key={b}>{b}</li>
                ))}
                {mode === "PERUSAHAAN" && (
                  <li style={{ color: "#0f172a", fontWeight: 600 }}>Untuk 1 tim / departemen</li>
                )}
              </ul>
              {hasActiveMentoring ? (
                <Link to="/mentoring" className="button" data-testid={`view-privat-${p.slug}`}>
                  Lihat Privat Saya <ArrowRight size={16} />
                </Link>
              ) : (
                <Link
                  to={user ? `/privat/checkout/${p.slug}?mode=${mode}` : `/login?next=${encodeURIComponent(`/privat/checkout/${p.slug}?mode=${mode}`)}`}
                  className="button"
                  data-testid={`privat-pick-${p.slug}`}
                  onClick={() => window.mastofaTrack?.("BeginCheckout", { content_name: p.title, value: showPrice, currency: "IDR" })}
                >
                  {user ? "Pilih Paket" : "Login & Pilih"} <ArrowRight size={16} />
                </Link>
              )}
            </div>
            );
          })}
        </div>
        <p className="privat-note">
          {c("pricing.note")}{" "}
          <a href={corpWaLink} target="_blank" rel="noreferrer" data-testid="corp-wa-note">
            {c("pricing.note_link")}
          </a>.
        </p>
      </section>

      {/* Informational: alternatif format — 1 baris link (bukan cross-sell agresif) */}
      <section data-testid="privat-alternative-link" style={{ padding: "40px 20px" }}>
        <div style={{
          background: "#f8fafc", border: "1px solid #e2e8f0",
          borderRadius: 12, padding: 24, textAlign: "center",
          maxWidth: 720, margin: "0 auto",
        }}>
          <small style={{ color: "#64748b", letterSpacing: 1, textTransform: "uppercase" }}>
            {c("alternative.kicker")}
          </small>
          <p style={{ marginTop: 8, marginBottom: 16, color: "#334155" }}>
            {c("alternative.body")}
          </p>
          <Link to={c("alternative.cta_to")} className="button-ghost" data-testid="privat-see-vip">
            {c("alternative.cta")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <section className="privat-corporate">
        <div className="privat-corp-inner">
          <span className="privat-eyebrow">{c("corporate.eyebrow")}</span>
          <h2>{c("corporate.title")}</h2>
          <div className="privat-corp-price">
            <span className="strike">{rupiah(settings.corporate_price_regular || 10000000)}</span>
            <b>{rupiah(settings.corporate_price || 5000000)}</b>
          </div>
          <a
            href={corpWaLink}
            target="_blank"
            rel="noreferrer"
            className="button"
            data-testid="corporate-wa-btn"
          >
            <MessageCircle size={16} /> {c("corporate.cta")}
          </a>
        </div>
      </section>

      {hasActiveMentoring && (
        <section className="privat-booking-cta" data-testid="booking-cta">
          <div className="section-head">
            <span className="section-kicker">Atur Jadwal Privat</span>
            <h2>Anda sudah punya paket aktif</h2>
            <p>Atur jadwal sesi lewat Member Area atau langsung WhatsApp Admin.</p>
          </div>
          <div className="privat-booking-actions">
            <Link to="/mentoring" className="button" data-testid="go-member-area">
              Buka Member Area <ArrowRight size={16} />
            </Link>
            <a
              href={bookingWaLink}
              target="_blank"
              rel="noreferrer"
              className="button button-ghost"
              data-testid="booking-wa"
            >
              <MessageCircle size={16} /> Atur Jadwal via WhatsApp
            </a>
          </div>
          <small className="privat-note">Nomor Admin: {adminWa}</small>
        </section>
      )}

      <section className="privat-faq">
        <div className="section-head">
          <span className="section-kicker">{c("faq.kicker")}</span>
          <h2>{c("faq.title")}</h2>
        </div>
        <div className="privat-faq-list">
          {PRIVAT_FAQ.map((f, i) => (
            <div key={i} className={`privat-faq-item ${openFaq === i ? "open" : ""}`}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                data-testid={`privat-faq-${i}`}
              >
                {f.q}
                <span>{openFaq === i ? "−" : "+"}</span>
              </button>
              {openFaq === i && <p>{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      <section className="privat-final-cta">
        <h2>{c("final.title")}</h2>
        <p>{c("final.body")}</p>
        <div className="privat-hero-cta">
          <a href={hasActiveMentoring ? "/mentoring" : "#pricing"} className="button button-large" data-testid="final-cta-primary">
            {hasActiveMentoring ? "Lihat Privat Saya" : c("final.cta")} <ArrowRight size={18} />
          </a>
          <a
            href={waLink(adminWa, "Halo Admin Mas Tofa, saya ingin bertanya tentang Privat 1-on-1.")}
            target="_blank"
            rel="noreferrer"
            className="button button-ghost"
            data-testid="final-cta-wa"
          >
            <MessageCircle size={16} /> {c("final.cta_wa")}
          </a>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="privat-sticky-cta" data-testid="privat-sticky-cta">
        {hasActiveMentoring ? (
          <Link to="/mentoring" className="button button-large">
            Lihat Sesi Privat <ArrowRight size={16} />
          </Link>
        ) : user ? (
          <a href="#pricing" className="button button-large">
            Pilih Paket <ArrowRight size={16} />
          </a>
        ) : (
          <Link to="/login?next=/privat" className="button button-large">
            Login / Daftar untuk Beli <ArrowRight size={16} />
          </Link>
        )}
      </div>

      <Footer />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Checkout                                                                  */
/* -------------------------------------------------------------------------- */
export function PrivatCheckout() {
  const { slug } = useParams();
  const nav = useNavigate();
  const user = getUser();
  const [pkg, setPkg] = useState(null);
  const [settings, setSettings] = useState({});
  const [methods, setMethods] = useState({ available: false, methods: [], message: "" });
  const initialMode = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("mode") === "PERUSAHAAN")
    ? "PERUSAHAAN" : "INDIVIDU";
  const [form, setForm] = useState({
    mode: initialMode,
    extra_participants: 0,
    whatsapp: "",
    city: "",
    address: "",
    payment_method: "",
  });
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api("/mentoring/packages").then((all) => {
      const found = all.find((x) => x.slug === slug);
      setPkg(found || null);
    });
    api("/mentoring/settings").then(setSettings).catch(() => {});
  }, [slug]);

  const extraPrice = Number(settings.extra_participant_price || 100000);
  const basePrice = pkg
    ? (form.mode === "PERUSAHAAN" ? Number(pkg.price) * 2 : Number(pkg.price))
    : 0;
  const total = basePrice + Number(form.extra_participants || 0) * extraPrice;

  useEffect(() => {
    if (!total) return;
    api(`/payments/methods?amount=${total}`)
      .then(setMethods)
      .catch(() => setMethods({ available: false, methods: [], message: "Metode belum tersedia." }));
  }, [total]);

  if (!user) {
    nav(`/login?next=/privat/checkout/${slug}`, { replace: true });
    return null;
  }
  if (!pkg) return <Loading label="Memuat paket..." />;

  const submit = async () => {
    setError("");
    if (!terms) {
      setError("Centang persetujuan syarat & ketentuan.");
      return;
    }
    if (!form.payment_method) {
      setError("Pilih metode pembayaran.");
      return;
    }
    if (!form.whatsapp || form.whatsapp.length < 8) {
      setError("Nomor WhatsApp wajib diisi (minimal 8 digit).");
      return;
    }
    setBusy(true);
    try {
      const r = await api("/payments/checkout-mentoring", {
        method: "POST",
        data: { package_slug: pkg.slug, ...form, extra_participants: Number(form.extra_participants) },
      });
      window.mastofaTrack?.("Purchase", { value: total, currency: "IDR", content_type: "mentoring" });
      if (r.paid) {
        nav("/dashboard?tab=mentoring", { replace: true });
      } else if (r.method === "BCA_MANUAL" && r.merchant_order_id) {
        nav(`/payment/${r.merchant_order_id}`);
      } else if (r.payment_url) {
        window.location.href = r.payment_url;
      } else {
        setError("Belum ada URL pembayaran, silakan coba lagi.");
      }
    } catch (e) {
      setError(e?.message || "Checkout gagal, coba lagi.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="checkout">
      <Header />
      <main className="checkout-main">
        <div className="checkout-left">
          <span className="section-kicker">Privat 1-on-1 · Checkout</span>
          <h1>{pkg.title} — MAS TOFA</h1>
          <p className="privat-checkout-sub">{pkg.tagline} · {pkg.sessions} sesi</p>

          <div className="checkout-block">
            <b>1. Jenis bimbingan</b>
            <div className="row-2">
              {["INDIVIDU", "PERUSAHAAN"].map((m) => (
                <label
                  key={m}
                  className={`radio-card ${form.mode === m ? "active" : ""}`}
                  data-testid={`mode-${m}`}
                >
                  <input
                    type="radio"
                    name="mode"
                    value={m}
                    checked={form.mode === m}
                    onChange={() => setForm({ ...form, mode: m })}
                  />
                  <span>{m === "INDIVIDU" ? "Individu" : "Perusahaan / Tim"}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="checkout-block">
            <b>2. Data diri</b>
            <input
              placeholder="WhatsApp aktif (mis. 6281234567890)"
              value={form.whatsapp}
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              data-testid="input-wa"
            />
            <input
              placeholder="Kota"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              data-testid="input-city"
            />
            <input
              placeholder="Alamat lengkap"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="input-address"
            />
          </div>

          <div className="checkout-block">
            <b>3. Tambah peserta? (+{rupiah(extraPrice)} / orang)</b>
            <input
              type="number"
              min="0"
              max="5"
              value={form.extra_participants}
              onChange={(e) => setForm({ ...form, extra_participants: e.target.value })}
              data-testid="input-extras"
            />
          </div>

          <div className="checkout-block">
            <b>4. Metode pembayaran</b>
            {!methods.available ? (
              <p className="privat-note">{methods.message || "Sedang memuat..."}</p>
            ) : (
              <div className="method-grid" data-testid="method-grid">
                {methods.methods.map((m) => (
                  <label
                    key={m.paymentMethod}
                    className={`method-card ${form.payment_method === m.paymentMethod ? "active" : ""}`}
                    data-testid={`method-${m.paymentMethod}`}
                  >
                    <input
                      type="radio"
                      name="pm"
                      value={m.paymentMethod}
                      checked={form.payment_method === m.paymentMethod}
                      onChange={() => setForm({ ...form, payment_method: m.paymentMethod })}
                    />
                    <img src={m.paymentImage} alt={m.paymentName} />
                    <span>{m.paymentName}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <label className="terms">
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              data-testid="terms-check"
            />
            Saya setuju dengan syarat &amp; ketentuan privat mentoring
          </label>
          {error && <div className="error" data-testid="checkout-error">{error}</div>}
          <button
            className="button button-large"
            disabled={busy}
            onClick={submit}
            data-testid="privat-pay-btn"
          >
            {busy ? "Memproses..." : `Bayar ${rupiah(total)}`}
          </button>
        </div>

        <aside className="checkout-summary" data-testid="privat-checkout-summary">
          <h4>Ringkasan Pesanan</h4>
          <div className="checkout-row">
            <span>{pkg.title}</span>
            <b>{rupiah(pkg.price)}</b>
          </div>
          {Number(form.extra_participants) > 0 && (
            <div className="checkout-row">
              <span>Extra peserta ×{form.extra_participants}</span>
              <b>{rupiah(Number(form.extra_participants) * extraPrice)}</b>
            </div>
          )}
          <hr />
          <div className="checkout-row total">
            <span>Total</span>
            <b>{rupiah(total)}</b>
          </div>
          <ul className="checkout-perks">
            <li><CheckCircle2 size={14} /> {pkg.sessions} sesi via Zoom (1-2 jam)</li>
            <li><CheckCircle2 size={14} /> Remote hands-on ke dashboard iklanmu</li>
            <li><CheckCircle2 size={14} /> Garansi 100% uang kembali</li>
          </ul>
        </aside>
      </main>
      <Footer />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  VIP Telegram Landing — Duitku checkout + auto-invite link                 */
/* -------------------------------------------------------------------------- */
export function VipLanding() {
  const [settings, setSettings] = useState({});
  const [status, setStatus] = useState(null);
  const [methods, setMethods] = useState({ available: false, methods: [] });
  const [pm, setPm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [terms, setTerms] = useState(false);
  const user = getUser();
  const { t: c, list } = useSiteContent("vip");

  useEffect(() => {
    api("/mentoring/settings").then(setSettings).catch(() => {});
    if (user?.id) api("/me/vip").then(setStatus).catch(() => {});
  }, [user?.id]);

  const price = settings.vip_price || 59900;
  const P = (s) => String(s || "").replace("{price}", rupiah(price));
  const tgUrl = settings.vip_telegram_url || "https://t.me/MasTofa_Bot";
  const ytId = youtubeId(settings.vip_youtube_url || "");

  useEffect(() => {
    if (!price) return;
    api(`/payments/methods?amount=${price}`).then(setMethods).catch(() => {});
  }, [price]);

  useSeo({
    title: `VIP Member — Grup Telegram Mentoring Harian ${rupiah(price)}/bulan`,
    description: `Gabung VIP Member MASTOFA.COM: grup Telegram konsultasi harian, materi eksklusif, dan Zoom bulanan. Hanya ${rupiah(price)}/bulan. Auto-invite setelah pembayaran.`,
    path: "/vip",
    jsonLd: jsonLdService({
      name: "VIP Member — Grup Telegram Mentoring Harian",
      description:
        "Membership bulanan berisi akses grup Telegram konsultasi harian dengan Mas Tofa, materi eksklusif, dan sesi Zoom bulanan.",
      price,
      path: "/vip",
    }),
    jsonLdId: "seo-jsonld-vip",
  });

  const buy = async () => {
    if (!user?.id) {
      window.location.href = "/login?next=/vip";
      return;
    }
    if (!pm) return setError("Pilih metode pembayaran.");
    if (!terms) return setError("Anda harus menyetujui Syarat & Ketentuan.");
    setBusy(true);
    setError("");
    try {
      const r = await api("/payments/checkout-vip", {
        method: "POST",
        data: { payment_method: pm },
      });
      window.mastofaTrack?.("InitiateCheckout", {
        content_name: "VIP Member",
        value: price,
        currency: "IDR",
      });
      if (r.method === "BCA_MANUAL" && r.merchant_order_id) {
        window.location.href = `/payment/${r.merchant_order_id}`;
      } else if (r.payment_url) {
        window.location.href = r.payment_url;
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Checkout gagal.");
    } finally {
      setBusy(false);
    }
  };

  const PROBLEM_ICONS = [<Ban size={22} />, <Search size={22} />, <ClipboardList size={22} />, <Target size={22} />];
  const BENEFIT_ICONS = [<MessageCircle size={22} />, <Sparkles size={22} />, <Video size={22} />, <UserCheck size={22} />, <ShieldCheck size={22} />, <Wrench size={22} />];
  const problems = list("problem.items").map((p, i) => ({ ...p, icon: PROBLEM_ICONS[i % PROBLEM_ICONS.length] }));
  const benefits = list("benefits.items").map((b, i) => ({ ...b, icon: BENEFIT_ICONS[i % BENEFIT_ICONS.length] }));
  const vipFaq = list("faq.items");

  const isActive = status?.active && status.invite_link;

  return (
    <div className="vip-page">
      <Header />

      {/* HERO */}
      <section className="vip-hero-v2">
        <div className="vip-hero-copy">
          <span className="section-kicker">{c("hero.kicker")}</span>
          <h1>{rich(c("hero.title"), "em")}</h1>
          <p>{c("hero.body")}</p>
          <div className="vip-hero-price">
            <div className="vip-price-box">
              <small>{c("hero.price_label")}</small>
              <b>{rupiah(price)}<span>/bulan</span></b>
              <small>{c("hero.price_sub")}</small>
            </div>
            <ul className="vip-hero-bullets">
              {list("hero.bullets").map((b, i) => (<li key={i}><CheckCircle2 size={16} /> {b.text}</li>))}
            </ul>
          </div>
        </div>

        <div className="vip-hero-cta" id="join">
          {isActive ? (
            <div className="vip-active-box" data-testid="vip-active">
              <CheckCircle2 size={22} />
              <div>
                <b>Membership aktif</b>
                <p>
                  Hingga{" "}
                  {status.expires_at
                    ? new Date(status.expires_at).toLocaleDateString("id-ID")
                    : "-"}
                </p>
              </div>
              <a href={status.invite_link} target="_blank" rel="noreferrer"
                 className="button" data-testid="vip-open-invite">
                <MessageCircle size={16} /> Buka Invite Link <ExternalLink size={14} />
              </a>
            </div>
          ) : methods.available && methods.methods.length > 0 ? (
            <div className="vip-buy-box" data-testid="vip-buy-box">
              <b>{c("hero.buy_title")}</b>
              <p className="muted" style={{ margin: "4px 0 14px", fontSize: 13 }}>
                {c("hero.buy_body")}
              </p>
              <div className="method-grid method-grid-vip">
                {methods.methods.slice(0, 6).map((m) => (
                  <label key={m.paymentMethod}
                    className={`method-card ${pm === m.paymentMethod ? "active" : ""}`}
                    data-testid={`vip-pm-${m.paymentMethod}`}>
                    <input type="radio" name="vip-pm"
                      checked={pm === m.paymentMethod}
                      onChange={() => setPm(m.paymentMethod)} />
                    <img src={m.paymentImage} alt={m.paymentName} />
                    <span>{m.paymentName}</span>
                  </label>
                ))}
              </div>
              <label className="terms" style={{ marginTop: 12 }}>
                <input type="checkbox" checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  data-testid="vip-terms-input" />
                Saya menyetujui{" "}
                <Link to="/terms">Syarat & Ketentuan</Link>,{" "}
                <Link to="/privacy">Privasi</Link>,{" "}
                <Link to="/refund">Refund</Link>, dan{" "}
                <Link to="/cancellation">Cancellation</Link>.
              </label>
              {error && <div className="error">{error}</div>}
              <button className="button button-large" onClick={buy}
                disabled={busy || !terms} data-testid="vip-buy-btn">
                {busy ? "Memproses…" : `Bayar ${rupiah(price)} & Gabung Sekarang`}
                <ArrowRight size={16} />
              </button>
              <small className="muted" style={{ display: "block", marginTop: 10, fontSize: 12 }}>
                <ShieldCheck size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                Kami tidak pernah meminta password, OTP, atau kredensial sensitif.
              </small>
            </div>
          ) : (
            <div className="vip-buy-box">
              <b>Metode pembayaran sedang diproses…</b>
              <p className="muted" style={{ fontSize: 13 }}>
                Sementara belum tersedia, silakan hubungi WhatsApp Admin.
              </p>
              <a href="https://wa.me/6285157009099?text=Hallo%20Admin%20Rara%2C%20saya%20mau%20gabung%20VIP%20Member."
                 target="_blank" rel="noreferrer" className="button">
                <MessageCircle size={16} /> Chat Admin
              </a>
            </div>
          )}
        </div>
      </section>

      {/* PROBLEM */}
      <section className="vip-section">
        <div className="section-head">
          <span className="section-kicker">{c("problem.kicker")}</span>
          <h2>{rich(c("problem.title"), "em")}</h2>
        </div>
        <div className="vip-grid-4">
          {problems.map((p) => (
            <div key={p.t} className="value-card">
              <div className="value-icon">{p.icon}</div>
              <b>{p.t}</b>
              <p>{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SOLUTION / BENEFIT */}
      <section className="vip-section vip-band-alt">
        <div className="section-head">
          <span className="section-kicker">{c("benefits.kicker")}</span>
          <h2>{rich(c("benefits.title"), "em")}</h2>
        </div>
        <div className="vip-grid-3">
          {benefits.map((b) => (
            <div key={b.t} className="value-card">
              <div className="value-icon">{b.icon}</div>
              <b>{b.t}</b>
              <p>{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="vip-section">
        <div className="section-head">
          <span className="section-kicker">{c("how.kicker")}</span>
          <h2>{rich(c("how.title"), "em")}</h2>
        </div>
        <ol className="svc-flow-list">
          {list("how.steps").map((s, i) => (<li key={i}><b>{P(s.title)}</b><span>{P(s.body)}</span></li>))}
        </ol>
        {ytId && (
          <div className="vip-video" data-testid="vip-video-wrap" style={{ marginTop: 30 }}>
            <iframe width="100%" height="100%"
              src={`https://www.youtube.com/embed/${ytId}`}
              title="Tutorial VIP Member" frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen />
          </div>
        )}
      </section>

      {/* Informational: butuh format lain? — 1 baris link, bukan tabel perbandingan */}
      <section className="vip-section" data-testid="vip-alternative-link">
        <div style={{
          background: "#f8fafc", border: "1px solid #e2e8f0",
          borderRadius: 12, padding: 24, textAlign: "center",
          maxWidth: 720, margin: "0 auto",
        }}>
          <small style={{ color: "#64748b", letterSpacing: 1, textTransform: "uppercase" }}>
            {c("alternative.kicker")}
          </small>
          <p style={{ marginTop: 8, marginBottom: 16, color: "#334155" }}>
            {c("alternative.body")}
          </p>
          <Link to={c("alternative.cta_to")} className="button-ghost" data-testid="vip-see-privat">
            {c("alternative.cta")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="vip-section">
        <div className="svc-notes">
          {list("transparency.items").map((n, i) => (
            <div key={i}>
              <b>{n.title}</b>
              <p>{n.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="vip-section vip-band-alt">
        <div className="section-head">
          <span className="section-kicker">{c("faq.kicker")}</span>
          <h2>{c("faq.title")}</h2>
        </div>
        <div className="faq-list">
          {vipFaq.map((f, i) => (
            <details key={i} className="faq-item" data-testid={`vip-faq-${i}`}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="vip-final-cta">
        <div>
          <span className="section-kicker">{c("final.kicker")}</span>
          <h2>{rich(c("final.title"), "em")}</h2>
          <p>{P(c("final.body"))}</p>
        </div>
        <a href="#join" className="button button-large">
          {c("final.cta")} <ArrowRight size={16} />
        </a>
      </section>

      <Footer />
    </div>
  );
}
